@use 'styles/mixins';
@use 'styles/variables';
@use 'styles/shared.module';

$background-color: white;
$background-color-disabled: rgb(229 229 233);

$base600: rgb(153 153 169);
$label: rgb(23 29 41);

$border-color: $base600;
$border-color-focused: var(--color-brand);
$border-color-disabled: grey;
$border-color-invalid: var(--color-danger);

.form.readonly form > * + * {
  margin-top: 0.5rem;
}

.form {
  accent-color: variables.$blue-primary;

  form {
    > * + * {
      margin-top: 3rem;
    }

    > * + *:has(input:read-only, textarea:read-only, select:disabled) {
      margin-top: 0.5rem;
    }

    fieldset {
      border: 0;
      border-top: 1px solid $border-color;

      legend {
        padding-right: 1em;
        font-size: 1.5rem;
        font-weight: bold;
      }

      > *:not(:first-child) {
        margin-top: 3rem;
      }

      > *:nth-child(2) {
        margin-top: 0; /* Override the previous rule for sub-forms with a label */
      }

      &.hasDisabled {
        > *:not(:first-child) {
          margin-top: 0;
        }
      }
    }

    input,
    .LabelContainer,
    select,
    .textFieldWithButtonGroup,
    .richTextInputPlaceholder {
      display: block;
      width: 100%;
      position: relative;
      height: fit-content;
    }

    input[type='file'] {
      display: none;
    }

    .radioInputList {
      display: flex;
      flex-direction: column;
      gap: 1em;

      label {
        display: grid;
        grid-template-columns: max-content 1fr max-content;
        grid-template-rows: auto auto;
        column-gap: 1em;
        align-items: center;

        .optionLabel {
          font-size: 1.4rem;
          grid-row: 1 / 2;
          grid-column: 2 / 3;
        }

        .optionDescription {
          font-size: 1.2rem;
          grid-row: 2 / 3;
          grid-column: 2 / 3;
        }
      }
    }

    label:has(input[type='checkbox']) {
      display: flex;
      align-items: center;
      gap: 1em;

      > * {
        width: unset;
      }
    }

    input,
    select,
    .textFieldWithButtonGroup,
    .richTextInputPlaceholder,
    .errorMessagePopover,
    .optionList {
      padding: 0.4em;

      border: 1px solid $border-color;
      border-radius: 5px;

      font-size: 1.4rem;

      background-color: $background-color;
      opacity: 1;
    }

    // for pills - making sure the input continues on the same line as the pills until it needs to wrap
    .textFieldWithButtonGroup input {
      flex: 1 1 120px;
      text-overflow: ellipsis;
    }

    .optionList {
      padding: 0;

      display: flex;
      flex-direction: column;

      overflow: hidden;
      overflow-y: scroll;
      max-height: 20rem;

      .option {
        width: 100%;
        padding: 0.4em;

        display: flex;
        justify-content: space-between;

        &:focus {
          background-color: variables.$blue-secondary-4;
        }
      }

      .highlighted {
        background-color: variables.$blue-secondary-4;
      }
    }

    // if the content is too long, cut it
    .richTextInputPlaceholder {
      padding: 0;
      min-height: 9rem;
      max-height: 30rem;
      overflow-y: auto;
    }

    .valueLabel {
      font-style: italic;
    }

    .textFieldWithButtonGroup {
      display: flex;
      padding: unset;

      flex-direction: row;
      align-items: center;

      &:not(:has(.icon)) {
        flex-wrap: wrap;
      }

      input,
      .valueLabel {
        border: unset;
        font-size: 1.5rem;
        padding: 5px;
        line-height: 1.32em;
        color: rgb(0 0 40);
      }

      .valueLabel {
        min-width: fit-content;
      }

      button {
        background: none;
      }

      .icon {
        box-sizing: content-box;
        padding: 0.4rem;

        background: none;

        color: $border-color;
      }

      a {
        width: 100%;
        text-decoration: unset !important;
        cursor: pointer;
      }
    }

    input:invalid,
    input.invalid,
    select.invalid,
    textarea.invalid,
    .textFieldWithButtonGroup:has(input:invalid),
    .textFieldWithButtonGroup:has(input.invalid),
    .richTextInvalid {
      border-color: var(--color-danger);
    }

    input[type='checkbox']:invalid,
    input[type='checkbox'].invalid {
      box-shadow: 0 0 0.1rem 0.1rem var(--color-danger);
    }

    button:focus .icon {
      text-shadow: var(--color-brand) 0 0 5px;
      color: var(--color-brand);
    }

    .textFieldWithButtonGroup:has(input:disabled, input:read-only) {
      background-color: $background-color-disabled;
      border-color: $border-color-disabled;
      cursor: not-allowed;
    }

    input:focus,
    select:focus,
    .textFieldWithButtonGroup:has(~ * .optionList:focus-within),
    .textFieldWithButtonGroup:focus-within:not(:has(input:disabled)) {
      border-color: $border-color-focused !important;

      .icon {
        color: $border-color-focused;
      }
    }

    input:disabled {
      pointer-events: none;
    }

    input:disabled,
    input:read-only:not([type='checkbox'], [type='radio']),
    select:disabled,
    option:disabled,
    optgroup:disabled,
    textarea:disabled,
    textarea:read-only,
    .richTextInputReadOnly {
      background-color: $background-color-disabled;
      border-color: $border-color-disabled;
      cursor: not-allowed;
    }

    .textFieldWithButtonGroup > a > input:read-only,
    .textFieldWithButtonGroup > a > input:disabled {
      cursor: pointer;
      text-decoration: underline;
    }

    input[type='checkbox']:focus {
      box-shadow: 0 0 0.1rem 0.1rem $border-color-focused;
    }
  }

  &_error {
    font-size: 1.1rem;
    top: 100%; // move this down (font-size) + 0.5rem
    position: absolute;
    color: var(--color-danger);
    cursor: pointer;
    width: inherit;
  }

  &_description {
    font-size: 1.1rem;
    position: absolute;
    color: $border-color;
    width: inherit;
  }
}

.label {
  margin-bottom: 1px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.25rem;
  overflow: hidden;
  width: 100%;
  font-size: 1.3rem;

  .indicator {
    color: var(--color-danger);
    font-weight: bold;
    font-size: inherit;
    margin-left: 0.5rem;
  }
}

.ListContainer {
  &_Content {
    margin-top: 2.4rem;
    padding-bottom: 6.4rem;

    &_Nested {
      padding-bottom: 0;
    }
  }

  &_AddBtn {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem !important;
    gap: 1rem;
  }
}

.OptionalList {
  @include mixins.flex-column;
  border: 1px solid variables.$grey2;
  border-radius: 4px;
  margin-top: 0.25rem !important;

  &_ListContainer {
    @include mixins.flex-align-center;
    padding: 1.3rem 0.5rem;

    border: none;
    border-radius: 0;

    &_Error_Label {
      margin-top: 0.25rem !important;
      color: variables.$red-secondary !important;
    }

    &_Input {
      width: calc(100% - 2.4rem);

      border: none;
      border-radius: 0;

      font-size: 1.4rem;

      outline: none;

      &_ReadOnly {
        cursor: default;
      }
    }
  }

  &_DelBtn {
    position: relative;

    margin-right: 1.5rem !important;

    width: 2.4rem;
    height: 2.4rem;

    &::before {
      position: absolute;
      top: 50%;
      left: 50%;

      width: 4rem;
      height: 4rem;
      border-radius: 50%;

      background-color: rgb(246 107 121 / 10%);
      transform: translate(-50%, -50%) scale(0);

      content: '';
    }

    &:hover::before {
      transform: translate(-50%, -50%) scale(1);
    }

    &:hover path {
      fill: red;
    }
  }
}

.OptionalList_Error {
  border: 1px solid variables.$red-secondary;
}

.Img {
  top: 2px;
  position: relative;
  margin-left: 1rem;
}

.UnsetWidth {
  width: unset;
}

.TextArea {
  @include shared.Input;
  height: unset;
  min-height: 9rem;

  resize: none;

  &_WithoutMinHeight {
    height: unset;
    min-height: unset;
  }

  &_ReadOnly {
    pointer-events: none;
  }

  &_WithPostIcon {
    padding-right: 5.6rem;
  }

  &_Right {
    width: fit-content;
    margin-left: auto;
    margin-top: 0 !important;
  }
}

.Resizable {
  resize: vertical;
}

.Wrap {
  position: relative;

  &_PostIcon {
    position: absolute;
    top: 1.4rem;
    right: 1.6rem;
  }
}

.button {
  background-color: variables.$grey8;
  border: none;
  padding: 0.25rem 0.5rem;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  cursor: pointer;
  border-radius: 1rem;
  text-wrap: nowrap;
  margin-left: 0.5rem;

  // wrap the pill text in-case it gets too long
  white-space: normal;
  overflow-wrap: anywhere;
}

.button:hover {
  background-color: variables.$blue-secondary-4;
}

.pillSelectedForDeletion {
  background-color: variables.$red-secondary;
}

.CloseBtn {
  top: 0.3rem;
  position: relative;
}

.ShowNone {
  display: none;
}

.Show {
  display: block;
}
